import React, {
	useState,
	useEffect,
	useRef,
	useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { addEventListener, removeEventListener } from 'utils/dom/dom';

const TRANSPARENT_GIF =
	'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

let hasDOMOverride;

export const __setHasDOMOverride = (value) => {
	hasDOMOverride = typeof value === 'boolean' ? value : undefined;
};

export const hasDOM = () => {
	if (typeof hasDOMOverride === 'boolean') {
		return hasDOMOverride;
	}
	return typeof window !== 'undefined' && typeof document !== 'undefined';
};

export function inViewport(el) {
	if (!el) {
		return false;
	}

	const rect = el.getBoundingClientRect();

	return (
		rect.bottom >= -200 &&
		rect.right >= 0 &&
		rect.top <=
			(window.innerHeight || document.documentElement.clientHeight) +
				200 &&
		rect.left <= (window.innerWidth || document.documentElement.clientWidth)
	);
}

const windowEvents = ['scroll', 'resize'];

/**
 * HOC that lazy loads a component based on viewport visibility.
 * Converted from recompose to hooks internally.
 */
export default (WrappedComponent) => {
	function LazyLoadContainer(props) {
		const [hydrated, setHydrated] = useState(false);
		const [isVisible, setIsVisible] = useState(false);
		const elRef = useRef(null);

		const handleViewportChange = useCallback(() => {
			if (!isVisible && inViewport(elRef.current)) {
				setIsVisible(true);
			}
		}, [isVisible]);

		const src = (!hydrated || isVisible) ? props.src : TRANSPARENT_GIF;

		useEffect(() => {
			if (!hasDOM()) return;
			setHydrated(true);
			handleViewportChange();
			addEventListener(window, windowEvents, handleViewportChange);
			const timer = setTimeout(handleViewportChange, 75);
			return () => {
				removeEventListener(window, windowEvents, handleViewportChange);
				clearTimeout(timer);
			};
		}, [handleViewportChange]);

		return (
			<span
				ref={elRef}
				className="ph-image-wrapper"
			>
				<WrappedComponent
					{...props}
					src={src}
				/>
			</span>
		);
	}

	LazyLoadContainer.propTypes = {
		src: PropTypes.string,
	};

	LazyLoadContainer.displayName = `LazyLoad(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
	return LazyLoadContainer;
};

// Keep named exports for tests
export { inViewport as lazyLoad };
